import {OpeningTimes} from '../api/types'
import {formatOpeningTimes, FormatOpeningTimesOptions} from './pass-hours'

/**
 * The server refuses to create a booking (or a payment draft, or a saved-card charge) with
 * HTTP 409 `PASS_NOT_VALID_AT_THIS_TIME` when a participant carries a pass that is limited to
 * certain days/hours and the booking START is outside them. Everything about that refusal lives
 * here so the booking forms stay thin: detect it, build the one-line message, take the pass off.
 */
export const PASS_NOT_VALID_CODE = 'PASS_NOT_VALID_AT_THIS_TIME'

export type PassNotValidPass = {
  membershipBookingId?: string
  membershipId?: string
  name?: string
  openingTimes?: Partial<OpeningTimes> | null
  // Positions in the booking's participants list that carry this pass. Absent on older servers.
  participantIndexes?: number[]
}

export type PassNotValid = {
  message?: string
  passes: PassNotValidPass[]
}

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null

const asString = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined)

const asIndexes = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined
  }
  return value.filter((item): item is number => Number.isInteger(item) && (item as number) >= 0)
}

const toPass = (value: unknown): PassNotValidPass | null => {
  if (!isRecord(value)) {
    return null
  }
  const pass: PassNotValidPass = {
    membershipBookingId: asString(value.membershipBookingId),
    membershipId: asString(value.membershipId),
    name: asString(value.name),
    openingTimes: isRecord(value.openingTimes) ? (value.openingTimes as Partial<OpeningTimes>) : null,
    participantIndexes: asIndexes(value.participantIndexes),
  }
  return pass.membershipBookingId || pass.membershipId || pass.name ? pass : null
}

const isPassNotValidBody = (body: unknown): body is UnknownRecord =>
  isRecord(body) && (body.code === PASS_NOT_VALID_CODE || body.type === PASS_NOT_VALID_CODE)

// Where the body can sit, from whichever error the http layer throws: an axios error
// (`response.data`), an already-unwrapped response (`data`), the body itself, or a body that wraps
// the refusal in `errors[0]` like the promo refusals do.
const findBody = (error: unknown): UnknownRecord | null => {
  if (!isRecord(error)) {
    return null
  }
  const response = isRecord(error.response) ? error.response : null
  const status = response?.status ?? error.status
  if (typeof status === 'number' && status !== 409) {
    return null
  }
  const candidates: unknown[] = [response?.data, error.data, error]
  for (const candidate of candidates) {
    if (isPassNotValidBody(candidate)) {
      return candidate
    }
    if (isRecord(candidate) && Array.isArray(candidate.errors) && isPassNotValidBody(candidate.errors[0])) {
      return candidate.errors[0] as UnknownRecord
    }
  }
  return null
}

/** Reads the refusal out of whatever the http layer threw. null when it is anything else. */
export const getPassNotValidFromError = (error: unknown): PassNotValid | null => {
  const body = findBody(error)
  if (!body) {
    return null
  }
  const listed = Array.isArray(body.passes)
    ? body.passes.map(toPass).filter((p): p is PassNotValidPass => Boolean(p))
    : []
  // Older shape: the first pass is also spelled out at the top level.
  const passes = listed.length > 0 ? listed : [toPass(body)].filter((p): p is PassNotValidPass => Boolean(p))
  return {message: asString(body.message), passes}
}

export const isPassNotValidError = (error: unknown): boolean => findBody(error) !== null

export type PassNotValidMessageKey = 'pass_unavailable_now' | 'pass_removed_from_booking'

/** "This membership can't be used at this time: Gold (Mon–Fri 09:00–12:00). It was taken off this booking…" */
export const buildPassNotValidMessage = (
  passes: PassNotValidPass[],
  translate: (key: PassNotValidMessageKey) => string,
  hoursOptions: FormatOpeningTimesOptions,
): string => {
  const seen = new Set<string>()
  const parts: string[] = []
  passes.forEach((pass) => {
    const hours = formatOpeningTimes(pass.openingTimes, hoursOptions)
    const part = [pass.name, hours && (pass.name ? `(${hours})` : hours)].filter(Boolean).join(' ')
    if (part && !seen.has(part)) {
      seen.add(part)
      parts.push(part)
    }
  })
  const head = translate('pass_unavailable_now')
  const named = parts.length > 0 ? `${head}: ${parts.join(', ')}` : head
  return `${named}. ${translate('pass_removed_from_booking')}`
}

export type PassCarrier = {
  membershipBookingId?: string | null
  membershipId?: string | null
  membership?: {uid?: string; membershipBookingId?: string} | null
  membershipActivity?: unknown
  coverEquipment?: boolean
}

const bookingIdOf = (participant: PassCarrier) =>
  participant.membershipBookingId || participant.membership?.membershipBookingId || undefined

export const hasPass = (participant: PassCarrier): boolean =>
  Boolean(bookingIdOf(participant) || participant.membershipId || participant.membership?.uid)

const carriesPass = (participant: PassCarrier, pass: PassNotValidPass): boolean => {
  if (!pass.membershipBookingId && !pass.membershipId) {
    // The server named the participants but not the pass: trust it, as long as they hold any pass.
    return hasPass(participant)
  }
  const bookingId = bookingIdOf(participant)
  if (pass.membershipBookingId) {
    return bookingId === pass.membershipBookingId
  }
  // No booking id to compare: fall back to the membership product, but only if there is one.
  const membershipId = participant.membershipId || participant.membership?.uid
  return Boolean(pass.membershipId) && membershipId === pass.membershipId
}

const stripPass = <P extends PassCarrier>(participant: P): P => ({
  ...participant,
  membershipBookingId: undefined,
  membershipId: undefined,
  membership: null,
  membershipActivity: null,
  coverEquipment: undefined,
})

/**
 * Takes the named passes off the participants that carry them, so the booking is quoted and paid at
 * the full price. Goes by `participantIndexes` when the server gave them; when it did not (or they
 * point at nobody carrying that pass) it goes by the pass itself, so a refused booking can never be
 * resubmitted unchanged. Returns the same array when nothing was removed.
 */
export const removePassesFromParticipants = <P extends PassCarrier>(
  participants: P[],
  passes: PassNotValidPass[],
): {participants: P[]; removed: number} => {
  const drop = new Set<number>()
  passes.forEach((pass) => {
    const indexes = (pass.participantIndexes || []).filter((index) => index < participants.length)
    const byIndex = indexes.filter((index) => carriesPass(participants[index], pass))
    if (byIndex.length > 0) {
      byIndex.forEach((index) => drop.add(index))
      return
    }
    participants.forEach((participant, index) => {
      if (carriesPass(participant, pass)) {
        drop.add(index)
      }
    })
  })
  if (drop.size === 0) {
    return {participants, removed: 0}
  }
  return {
    participants: participants.map((participant, index) => (drop.has(index) ? stripPass(participant) : participant)),
    removed: drop.size,
  }
}
